import { InvoiceAgingPlacementRow } from '../gql';
import {
  BucketKey,
  bucketOf,
  computeAging,
  isPastDue,
  rowSortRank,
} from './aging';

export type AllBuckets = 'all' | BucketKey;

export interface BucketSegment {
  key: BucketKey;
  amount: number;
}

export interface CompanyGroup {
  companyId: string;
  companyName: string;
  rows: InvoiceAgingPlacementRow[];
  /** Σ netCommission where status !== PAID */
  outstanding: number;
  /** Σ netCommission where INVOICED && aging > payable term (overdue) */
  pastDue: number;
  segments: BucketSegment[];
}

export interface KpiData {
  /** Σ netCommission where status !== PAID */
  totalOutstanding: number;
  /** Σ netCommission where INVOICED && aging > payable term (overdue) */
  pastDue: number;
  /** Σ netCommission where overdue && aging > 90 */
  over90: number;
  /** per-bucket Σ netCommission of overdue INVOICED rows */
  distribution: Record<BucketKey, number>;
  count: number;
}

const BUCKET_KEYS: BucketKey[] = ['current', 'b30', 'b60', 'b90', 'b120', 'b120p'];

function emptyDistribution(): Record<BucketKey, number> {
  return { current: 0, b30: 0, b60: 0, b90: 0, b120: 0, b120p: 0 };
}

// Filter rows by bucket (returns all when bucket === 'all')
export function applyBucketFilter(
  rows: InvoiceAgingPlacementRow[],
  bucket: AllBuckets,
  today: Date
): InvoiceAgingPlacementRow[] {
  if (bucket === 'all') return rows;
  return rows.filter((row) => {
    if (!isPastDue(row, today)) return false;
    const aging = computeAging(row, today);
    if (aging === null) return false;
    return bucketOf(aging) === bucket;
  });
}

// Compute KPIs over the given set of rows
export function computeKpis(
  rows: InvoiceAgingPlacementRow[],
  today: Date
): KpiData {
  let totalOutstanding = 0;
  let pastDue = 0;
  let over90 = 0;
  const distribution = emptyDistribution();

  for (const row of rows) {
    const fee = row.netCommission ?? 0;

    if (row.placementStatus !== 'PAID') {
      totalOutstanding += fee;
    }

    if (isPastDue(row, today)) {
      const aging = computeAging(row, today);
      if (aging !== null) {
        distribution[bucketOf(aging)] += fee;
        pastDue += fee;
        if (aging > 90) {
          over90 += fee;
        }
      }
    }
  }

  return { totalOutstanding, pastDue, over90, distribution, count: rows.length };
}

// Group rows by companyId, compute subtotals, sort companies
export function groupByCompany(
  rows: InvoiceAgingPlacementRow[],
  today: Date
): CompanyGroup[] {
  const map = new Map<string, CompanyGroup>();

  for (const row of rows) {
    const key = row.companyId ?? '__unknown__';
    if (!map.has(key)) {
      map.set(key, {
        companyId: key,
        companyName: row.companyName ?? '—',
        rows: [],
        outstanding: 0,
        pastDue: 0,
        segments: [],
      });
    }
    const group = map.get(key)!;
    group.rows.push(row);
  }

  const groups: CompanyGroup[] = [];

  map.forEach((group) => {
    // Sort rows within group
    group.rows.sort((a, b) => rowSortRank(b, today) - rowSortRank(a, today));

    // Subtotals
    let outstanding = 0;
    let pastDue = 0;
    const bucketMap = emptyDistribution();

    for (const row of group.rows) {
      const fee = row.netCommission ?? 0;
      if (row.placementStatus !== 'PAID') {
        outstanding += fee;
      }
      if (isPastDue(row, today)) {
        const aging = computeAging(row, today);
        if (aging !== null) {
          pastDue += fee;
          bucketMap[bucketOf(aging)] += fee;
        }
      }
    }

    group.outstanding = outstanding;
    group.pastDue = pastDue;
    group.segments = BUCKET_KEYS.filter((k) => bucketMap[k] > 0).map((k) => ({
      key: k,
      amount: bucketMap[k],
    }));

    groups.push(group);
  });

  // Company sort: pastDue$ DESC, then outstanding$ DESC, then companyName ASC (deterministic)
  groups.sort((a, b) => {
    if (b.pastDue !== a.pastDue) return b.pastDue - a.pastDue;
    if (b.outstanding !== a.outstanding) return b.outstanding - a.outstanding;
    return a.companyName.localeCompare(b.companyName);
  });

  return groups;
}
